import { Fragment, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/cn';
import { coincideBusqueda, prepararBusqueda } from '@/shared/lib/tablaOrden';
import type { ReportPreviewResponse } from './api';

/**
 * Productos entregados con garantía (server: GET /api/reportes/garantias).
 * El server manda una fila por entrega, ya ordenada por las unidades del
 * producto de mayor a menor; acá se agrupa por producto y cada uno se abre
 * para ver sus entregas. El Excel (genérico) lleva todas las entregas.
 */
type Fila = Record<string, string | number>;

const DETALLE = ['Venta', 'Cliente', 'N° Serie', 'Cantidad', 'Entrega', 'Vence', 'Garantía', 'Estado'] as const;

export function GarantiasTable({
  data,
  busqueda,
  loading,
  error,
}: {
  data: ReportPreviewResponse | undefined;
  busqueda: string;
  loading: boolean;
  error: boolean;
}) {
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());
  const grupos = useMemo(() => {
    const b = prepararBusqueda(busqueda);
    const rows = (data?.rows ?? []).filter((r) => !b || coincideBusqueda(Object.values(r), b));
    const out = new Map<string, { codigo: string; articulo: string; unidades: number; vigentes: number; filas: Fila[] }>();
    for (const r of rows) {
      const k = `${r['Código']}|${r['Artículo']}`;
      let g = out.get(k);
      if (!g) {
        g = { codigo: String(r['Código'] ?? ''), articulo: String(r['Artículo'] ?? ''), unidades: 0, vigentes: 0, filas: [] };
        out.set(k, g);
      }
      const cant = Number(r.Cantidad) || 0;
      g.unidades += cant;
      if (r.Estado === 'Vigente') g.vigentes += cant;
      g.filas.push(r);
    }
    // Con búsqueda, el orden se recalcula sobre lo que quedó.
    return [...out.entries()].sort((a, b) => b[1].unidades - a[1].unidades);
  }, [data, busqueda]);

  if (loading) return <Skeleton className="h-64 w-full rounded-2xl" />;
  if (error) return <p className="text-sm text-destructive">No se pudo cargar el reporte.</p>;
  if (grupos.length === 0) {
    return (
      <div className="rounded-2xl border bg-card p-8 text-center text-sm text-muted-foreground">
        No hay productos entregados con garantía en el período.
      </div>
    );
  }

  const alternar = (k: string) =>
    setAbiertos((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  return (
    <div className="min-h-0 flex-1 overflow-auto rounded-2xl border bg-card">
      <table className="w-full text-sm">
        <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="w-10 px-3 py-3" />
            <th className="px-3 py-3 font-semibold">Código</th>
            <th className="px-3 py-3 font-semibold">Artículo</th>
            <th className="px-3 py-3 text-right font-semibold">Unidades</th>
            <th className="px-3 py-3 text-right font-semibold">Vigentes</th>
            <th className="px-3 py-3 text-right font-semibold">Vencidas</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {grupos.map(([k, g]) => {
            const abierto = abiertos.has(k);
            return (
              <Fragment key={k}>
                <tr
                  className="cursor-pointer hover:bg-muted/40"
                  onClick={() => alternar(k)}
                  aria-expanded={abierto}
                >
                  <td className="px-3 py-3 text-muted-foreground">
                    {abierto ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
                  </td>
                  <td className="px-3 py-3 text-muted-foreground">{g.codigo || '—'}</td>
                  <td className="px-3 py-3 font-medium text-foreground">{g.articulo}</td>
                  <td className="px-3 py-3 text-right font-semibold tabular-nums">{g.unidades}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-emerald-700">{g.vigentes}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-muted-foreground">{g.unidades - g.vigentes}</td>
                </tr>
                {abierto && (
                  <tr>
                    <td />
                    <td colSpan={5} className="bg-muted/20 px-3 pb-3">
                      <table className="mt-2 w-full text-xs">
                        <thead className="text-left uppercase tracking-wide text-muted-foreground">
                          <tr>
                            {DETALLE.map((h) => (
                              <th key={h} className={cn('px-2 py-1.5 font-semibold', h === 'Cantidad' && 'text-right')}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {g.filas.map((r, i) => (
                            <tr key={i}>
                              {DETALLE.map((h) => (
                                <td
                                  key={h}
                                  className={cn(
                                    'px-2 py-1.5',
                                    h === 'Cantidad' && 'text-right tabular-nums',
                                    h === 'Estado' && (r.Estado === 'Vigente' ? 'text-emerald-700' : 'text-muted-foreground'),
                                  )}
                                >
                                  {r[h] === '' ? '—' : r[h]}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
